import { engine } from '../hooks/useEngine.ts';
import type { SavedSpark } from '../lib/storage.ts';
import { CloseIcon, HeartIcon, ReplayIcon } from './Icons.tsx';

interface Props {
  sparks: SavedSpark[];
  online: string[];
}

/**
 * Your sparks — the closest thing Phidio has to a friend list, and deliberately
 * temporary: entries live for twelve hours, then the door closes again.
 */
export function SparkList({ sparks, online }: Props) {
  if (sparks.length === 0) return null;

  const onlineCount = sparks.filter((spark) => online.includes(spark.token)).length;

  return (
    <section className="mt-16 w-full">
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-[0.2em] text-white/40">
          Your sparks
        </h2>
        <p className="text-xs text-white/35">
          {onlineCount > 0 ? `${onlineCount} online now` : 'we\u2019ll light up when they return'}
        </p>
      </div>

      <ul className="grid gap-2 sm:grid-cols-2">
        {sparks.map((spark) => {
          const isOnline = online.includes(spark.token);
          return (
            <li
              key={spark.token}
              className={`glass flex items-center gap-3 rounded-2xl p-3 transition ${
                isOnline ? 'ring-1 ring-phidio-500/40' : ''
              }`}
            >
              <span
                className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                  isOnline
                    ? 'bg-gradient-to-br from-phidio-400 to-phidio-700 text-white'
                    : 'bg-white/6 text-white/30'
                }`}
              >
                <HeartIcon filled width={18} height={18} />
                {isOnline && (
                  <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-ink-900 bg-emerald-400" />
                )}
              </span>

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{spark.name}</p>
                <p className="text-xs text-white/40">
                  {isOnline ? 'online now' : `sparked ${ago(spark.at)}`}
                </p>
              </div>

              <button
                type="button"
                onClick={() => void engine.reconnectTo(spark.token)}
                disabled={!isOnline}
                className="flex items-center gap-1.5 rounded-full bg-phidio-500 px-3.5 py-2 text-xs font-bold uppercase tracking-wide text-white transition active:scale-95 disabled:bg-white/6 disabled:text-white/25"
              >
                <ReplayIcon width={14} height={14} />
                {isOnline ? 'Talk' : 'Away'}
              </button>

              <button
                type="button"
                onClick={() => engine.forgetSpark(spark.token)}
                aria-label={`Forget ${spark.name}`}
                title={`Forget ${spark.name}`}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white/25 transition hover:bg-white/10 hover:text-white"
              >
                <CloseIcon width={14} height={14} />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ago(at: number): string {
  const minutes = Math.max(1, Math.round((Date.now() - at) / 60_000));
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
}
